import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAudioPlayer } from '../hooks/useAudio';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { api } from '../lib/api';
import { SophiaAvatar } from '../components/SophiaAvatar';
import { LanguageSelector } from '../components/LanguageSelector';
import { ShareWisdomButton } from '../components/ShareWisdomCard';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Sun, Volume2, Type, Clock, Sparkles, Bell, BellOff, Loader2, ImageIcon } from 'lucide-react';

export default function DailyAdvicePage() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { play } = useAudioPlayer();
  const { isSupported, isSubscribed, permission, isLoading: pushLoading, subscribe, unsubscribe, testPush } = usePushNotifications();
  const [settings, setSettings] = useState({
    enabled: true,
    message_type: 'text',
    time: '09:00',
    language: 'en',
  });
  const [dailyMessage, setDailyMessage] = useState(null);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [pushTestSent, setPushTestSent] = useState(false);

  useEffect(() => {
    api.getDailySettings().then(({ data }) => {
      setSettings(data);
    }).catch(console.error);
  }, []);

  const handlePushToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      const success = await subscribe();
      if (success) {
        // Send a test notification
        setPushTestSent(true);
        await testPush();
        setTimeout(() => setPushTestSent(false), 3000);
      }
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await api.updateDailySettings({ ...settings, language });
    } catch (err) {
      console.error('Save settings error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchDailyMessage = async () => {
    setIsLoadingMessage(true);
    try {
      const { data } = await api.getDailyMessage(language);
      setDailyMessage(data);
      if (data.audio_base64) {
        setIsSpeaking(true);
        const audio = play(`data:audio/mpeg;base64,${data.audio_base64}`);
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => setIsSpeaking(false);
      }
    } catch (err) {
      console.error('Daily message error:', err);
    } finally {
      setIsLoadingMessage(false);
    }
  };

  const shareMessage = async () => {
    if (!dailyMessage) return;
    const text = `"${dailyMessage.text}" — Sophia Einstein\n@sophia_einstein_the_wise`;
    
    // Use native share API (works on iOS/Android)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Sophia Einstein Daily Wisdom',
          text: text,
        });
      } catch (err) {
        // User cancelled or share failed - fallback to clipboard
        if (err.name !== 'AbortError') {
          copyToClipboard(text);
        }
      }
    } else {
      // Fallback for desktop - copy to clipboard
      copyToClipboard(text);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col noise-overlay">
      {/* Header */}
      <motion.header
        className="flex items-center justify-between px-6 py-4 border-b border-border/50 backdrop-blur-xl bg-background/80 sticky top-0 z-30"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <Button data-testid="daily-back-btn" variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-display text-xl font-semibold">{t('daily.title')}</h1>
        <LanguageSelector />
      </motion.header>

      <div className="flex-1 px-6 py-8 max-w-lg mx-auto w-full space-y-8">
        {/* Sophia */}
        <motion.div
          className="flex justify-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <SophiaAvatar variant="pink" size="lg" isSpeaking={isSpeaking} />
        </motion.div>

        {/* Today's message */}
        <motion.div
          className="bg-card/40 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Sun className="w-5 h-5 text-sophia-pink-500" />
            <h2 className="font-display text-lg font-semibold">{t('daily.todayMessage')}</h2>
          </div>

          <AnimatePresence mode="wait">
            {dailyMessage ? (
              <motion.div
                key="message"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <p className="font-accent text-lg italic text-foreground leading-relaxed" data-testid="daily-message-text">
                  "{dailyMessage.text}"
                </p>
                
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  {dailyMessage.audio_base64 && (
                    <button
                      data-testid="daily-play-audio-btn"
                      onClick={() => {
                        setIsSpeaking(true);
                        const audio = play(`data:audio/mpeg;base64,${dailyMessage.audio_base64}`);
                        audio.onended = () => setIsSpeaking(false);
                      }}
                      className="flex items-center gap-2 text-sm text-sophia-pink-500 hover:text-sophia-pink-300 transition-colors"
                    >
                      <Volume2 className="w-4 h-4" />
                      {t('daily.listen')}
                    </button>
                  )}
                  
                  {/* Share as Wisdom Card */}
                  <div className="flex items-center gap-2 text-sm text-mystic-teal-500">
                    <ImageIcon className="w-4 h-4" />
                    <ShareWisdomButton 
                      quote={dailyMessage.text} 
                      language={language} 
                      variant="full" 
                      className="!bg-transparent !px-0 hover:!bg-transparent hover:opacity-80"
                    />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div key="cta" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Button
                  data-testid="daily-get-wisdom-btn"
                  onClick={fetchDailyMessage}
                  disabled={isLoadingMessage}
                  className="w-full h-12 rounded-full bg-sophia-pink-500 hover:bg-sophia-pink-700 text-white font-display"
                >
                  {isLoadingMessage ? (
                    <div className="flex gap-1.5">
                      <motion.div className="w-2 h-2 bg-white rounded-full" animate={{ y: [0, -6, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0 }} />
                      <motion.div className="w-2 h-2 bg-white rounded-full" animate={{ y: [0, -6, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.15 }} />
                      <motion.div className="w-2 h-2 bg-white rounded-full" animate={{ y: [0, -6, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }} />
                    </div>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      {t('daily.getWisdom')}
                    </>
                  )}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Settings */}
        <motion.div
          className="bg-card/40 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl space-y-6"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-mystic-teal-500" />
            <h2 className="font-display text-lg font-semibold">{t('daily.settings')}</h2>
          </div>

          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <span className="font-body text-sm text-muted-foreground">
              {settings.enabled ? t('daily.enabled') : t('daily.disabled')}
            </span>
            <Switch
              data-testid="daily-enable-switch"
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>

          {/* Push Notifications toggle */}
          {isSupported && (
            <div className="flex items-center justify-between pt-2 border-t border-border/30">
              <div className="flex items-center gap-2">
                {isSubscribed ? (
                  <Bell className="w-4 h-4 text-sophia-pink-500" />
                ) : (
                  <BellOff className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="font-body text-sm text-muted-foreground">
                  {language === 'de' ? 'Push-Benachrichtigungen' : 'Push Notifications'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {pushLoading && <Loader2 className="w-4 h-4 animate-spin text-sophia-pink-500" />}
                {pushTestSent && (
                  <span className="text-xs text-green-500 font-body">
                    {language === 'de' ? 'Gesendet!' : 'Sent!'}
                  </span>
                )}
                <Switch
                  data-testid="daily-push-switch"
                  checked={isSubscribed}
                  onCheckedChange={handlePushToggle}
                  disabled={pushLoading || permission === 'denied'}
                />
              </div>
            </div>
          )}
          {permission === 'denied' && (
            <p className="text-xs text-red-400 font-body">
              {language === 'de' 
                ? 'Benachrichtigungen wurden blockiert. Bitte in den Browser-Einstellungen aktivieren.' 
                : 'Notifications blocked. Please enable in browser settings.'}
            </p>
          )}

          {/* Message type */}
          <div className="space-y-2">
            <label className="font-body text-sm text-muted-foreground">{t('daily.messageType')}</label>
            <div className="flex gap-3">
              <button
                data-testid="daily-type-text-btn"
                onClick={() => setSettings({ ...settings, message_type: 'text' })}
                className={`flex-1 h-12 rounded-full flex items-center justify-center gap-2 border transition-all font-body text-sm ${
                  settings.message_type === 'text'
                    ? 'bg-sophia-pink-500/20 border-sophia-pink-500 text-sophia-pink-500'
                    : 'border-border text-muted-foreground hover:border-sophia-pink-500/50'
                }`}
              >
                <Type className="w-4 h-4" /> {t('daily.text')}
              </button>
              <button
                data-testid="daily-type-voice-btn"
                onClick={() => setSettings({ ...settings, message_type: 'voice' })}
                className={`flex-1 h-12 rounded-full flex items-center justify-center gap-2 border transition-all font-body text-sm ${
                  settings.message_type === 'voice'
                    ? 'bg-mystic-teal-500/20 border-mystic-teal-500 text-mystic-teal-500'
                    : 'border-border text-muted-foreground hover:border-mystic-teal-500/50'
                }`}
              >
                <Volume2 className="w-4 h-4" /> {t('daily.voice')}
              </button>
            </div>
          </div>

          {/* Time */}
          <div className="space-y-2">
            <label className="font-body text-sm text-muted-foreground">{t('daily.time')}</label>
            <input
              data-testid="daily-time-input"
              type="time"
              value={settings.time}
              onChange={(e) => setSettings({ ...settings, time: e.target.value })}
              className="w-full h-12 px-5 rounded-full bg-background/50 border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-sophia-pink-500/50 font-body"
            />
          </div>

          <Button
            data-testid="daily-save-btn"
            onClick={saveSettings}
            disabled={isSaving}
            className="w-full h-12 rounded-full bg-mystic-teal-500 hover:bg-mystic-teal-700 text-white font-display"
          >
            {isSaving ? 'Saving...' : t('daily.save')}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
